import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import { KITS } from "@/data/kits";

const Kits = () => {
  const [active, setActive] = useState(KITS[0].slug);
  const kit = KITS.find((k) => k.slug === active);

  return (
    <div data-testid="kits-page">
      <SEO
        title="Pooja Kits — Hanuman, Shani, Shivji, Griha Pravesh & Lakshmi"
        description="Complete ritual solutions — hand-curated Hanuman, Shani, Shivji, Griha Pravesh and Lakshmi pooja kits. Every kit includes premium samagri and a step-by-step guide book."
        keywords="hanuman kit, shani kit, shivji kit, griha pravesh kit, lakshmi puja kit, complete pooja kit, sanatan dharma kits"
        path="/kits"
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHeading
            overline="Special Occasion Kits"
            title="Complete Ritual Solutions."
            subtitle="Each kit is built around a single occasion — measured, sealed and paired with a guide book so the ritual unfolds without a single guess."
          />
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="kit-tabs">
            {KITS.map((k) => (
              <button
                key={k.slug}
                onClick={() => setActive(k.slug)}
                data-testid={`kit-tab-${k.slug}`}
                className={`p-4 lg:p-5 text-left transition-all ${active === k.slug ? "text-white" : "text-[#3B2A1A] bg-[#F5EFE3] hover:opacity-80"}`}
                style={active === k.slug ? { background: k.color } : {}}
              >
                <p className="text-[9px] uppercase tracking-[0.22em] opacity-70">Kit</p>
                <p className="font-serif-display text-lg lg:text-xl mt-1 leading-tight">{k.name.replace(" Kit", "")}</p>
              </button>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-6">
              <div
                className="relative aspect-[4/5] overflow-hidden text-white flex flex-col justify-between p-10 lg:p-14"
                style={{ background: kit.color }}
                data-testid="kit-hero-card"
              >
                <img src={kit.image} alt={kit.name} className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-luminosity" />
                <div className="absolute inset-0 mandala-bg opacity-20" />
                <div className="relative">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-white/80">Puja Kit</p>
                  <h2 className="font-serif-display text-5xl lg:text-6xl mt-3 leading-[0.95]">{kit.name}</h2>
                  <p className="font-serif-display text-xl italic mt-3 text-white/90 max-w-md">{kit.tagline}</p>
                </div>
                <div className="relative">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/80">For</p>
                  <p className="text-sm text-white mt-2">{kit.occasion}</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
                <p className="overline">About this kit</p>
              <p className="text-lg leading-relaxed text-[#3B2A1A] mt-4 font-light">{kit.description}</p>

              <div className="mt-10">
                <p className="overline mb-4">What&apos;s Included</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5" data-testid="kit-includes">
                  {kit.includes.map((it) => (
                    <li key={it} className="text-sm text-[#3B2A1A] flex items-start gap-3">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full" style={{ background: kit.color }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10">
                <p className="overline mb-4">Benefits</p>
                <ul className="space-y-2.5">
                  {kit.benefits.map((b) => (
                    <li key={b} className="text-sm text-[#5C4A3A] flex items-start gap-3 font-light">
                      <span className="w-1.5 h-1.5 mt-2 rounded-full bg-[#C89D3C]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10">
                <p className="overline mb-4">How To Use</p>
                <ol className="space-y-3">
                  {kit.howTo.map((s, i) => (
                    <li key={i} className="text-sm text-[#5C4A3A] flex items-start gap-4 font-light">
                      <span className="font-serif-display text-2xl leading-none" style={{ color: kit.color }}>{i + 1}</span>
                      <span className="pt-1">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-12 p-6 bg-[#F5EFE3] border-l-2" style={{ borderColor: kit.color }}>
                <p className="overline">Included</p>
                <p className="font-serif-display text-xl text-[#3B2A1A] mt-1">A printed guide book — step by step.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kits;
